/**
 * Public login page — Google OAuth entry point for the SPA.
 * Accessible at /login. Brand look: soft radial glow over the app background,
 * centered card with logo + official-style Google button.
 */
import { authClient } from '../lib/auth-client'
import { useT } from '../i18n/language-provider'
import { BrandLogo } from '../components/brand-logo'

/** Official multi-color Google "G" mark. */
function GoogleG() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

export function LoginPage() {
  const t = useT()
  // Better Auth redirects denied/errored sign-ins back here with ?error=…
  // (blocked accounts never get a session — see api session-create hook).
  const loginError = new URLSearchParams(window.location.search).get('error')

  async function handleGoogleSignIn() {
    try {
      await authClient.signIn.social({ provider: 'google', callbackURL: '/', errorCallbackURL: '/login' })
    } catch (err: unknown) {
      console.error('Google sign-in failed:', err)
    }
  }

  return (
    <div id="page-login" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-app px-4">
      {/* Soft brand glows behind the card */}
      <div aria-hidden className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-brand/15 blur-3xl" />

      <div className="relative w-full max-w-sm rounded-2xl border border-border bg-surface p-8 shadow-xl">
        <div className="mb-6 flex flex-col items-center text-center">
          {/* Logo already carries the brand — no separate title text. */}
          <BrandLogo className="mb-3 h-10 w-auto" />
          <p className="text-sm text-muted">{t('login.subtitle')}</p>
        </div>

        {loginError && (
          <p className="mb-4 rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-center text-xs text-danger">
            {t('login.accessDenied')}
          </p>
        )}

        <button
          id="login-signin-btn"
          type="button"
          onClick={() => { void handleGoogleSignIn() }}
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-fg shadow-sm transition-colors hover:bg-hover focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <GoogleG />
          {t('login.signInWithGoogle')}
        </button>

        <p className="mt-6 text-center text-xs text-faint">{t('layout.tagline')} · Supernova JSC</p>
      </div>
    </div>
  )
}
